<template>
  <div
    class="icon-banner-tooltip"
    style="display: inline-block; position: relative"
    ref="rootEl"
  >
    <button class="icon-btn" @click="toggle" aria-label="Show info">
      <slot name="icon">
        <img
          :src="useAsset('img/credit-line/due-icon.svg')"
          alt="Due Icon"
          style="
            width: 18px;
            height: 18px;
            filter: hue-rotate(305.1deg) contrast(200%) saturate(270%);
          "
        />
      </slot>
    </button>
    <transition name="fade" @mouseleave="isOpen = false">
      <div v-if="isOpen" class="banner" :style="{ minWidth: bannerWidth }">
        <div class="arrow" />
        <span class="banner-text">{{ bannerText }}</span>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, watch } from "vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";

defineProps<{
  bannerText: string;
  bannerWidth?: string;
}>();

const emit = defineEmits<{
  onClick: (event: PointerEvent) => void;
}>();

const isOpen = ref<boolean>(false);
const rootEl = ref<HTMLElement | null>(null);

function toggle(event: PointerEvent) {
  emit("onClick", event);
  isOpen.value = !isOpen.value;
}

function handleOutside(event: MouseEvent | TouchEvent) {
  if (rootEl.value && !rootEl.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
}

watch(isOpen, (open) => {
  if (open) {
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
  } else {
    document.removeEventListener("mousedown", handleOutside);
    document.removeEventListener("touchstart", handleOutside);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleOutside);
  document.removeEventListener("touchstart", handleOutside);
});
</script>

<style scoped>
.icon-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  vertical-align: middle;
  outline: none;
  display: inline-flex;
  align-items: center;
}
.banner {
  position: absolute;
  top: 33px;
  left: 50%;
  transform: translateX(-50%);
  background: #1a1a1a;
  color: #fff;
  border-radius: 12px;
  padding: 12px 20px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 22px;
  font-family: Inter, Arial, sans-serif;
  font-weight: 400;
}
.arrow {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
  width: 18px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.arrow::after {
  content: "";
  color: #fff;
  width: 14px;
  height: 10px;
  background: none;
  border: none;
  clip-path: polygon(50% 100%, 0 0, 100% 0);
  background: #1a1a1a;
  margin: 0 auto;
}
.banner-text {
  white-space: pre-line;
  text-align: center;
  color: #fff;
  font-family: Inter, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
